import { Building2, Calendar, ChevronRight, DollarSign, FileText, MapPin, PenLine } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';


interface ContractCardProps {
  contract: any;
  onViewDetails: () => void;
  type: 'pending' | 'signed' | 'total';
}
export function ContractCard({ contract, onViewDetails ,type}: ContractCardProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };
  const navigate = useNavigate()

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (!dateString || Number.isNaN(date.getTime())) return 'Not set';

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isSigned = type === 'signed' || (type === 'total' && Boolean(contract.homeowner_signature_url));
  const isReadyToSign = type === 'pending' || (
    type === 'total' && !contract.homeowner_signature_url && Boolean(contract.contractor_signature_url)
  );
  const statusLabel = isSigned
    ? 'Signed'
    : isReadyToSign
      ? 'Awaiting your signature'
      : 'Contractor signature pending';
  const statusTone = isSigned ? 'signed' : isReadyToSign ? 'pending' : 'waiting';

  return (
    <article className="homeowner-contract-card">
      <header className="homeowner-contract-card__header">
        <div>
          <span className={`homeowner-contract-card__status homeowner-contract-card__status--${statusTone}`}>
            {statusLabel}
          </span>
          <h3>{contract.project_name || 'Untitled contract'}</h3>
          <div className="homeowner-contract-card__address">
            <MapPin aria-hidden="true" />
            <span>{contract.contract_address || 'Address not provided'}</span>
          </div>
        </div>
      </header>

      <div className="homeowner-contract-card__details">
        <div className="homeowner-contract-card__detail">
          <Building2 aria-hidden="true" />
          <div>
            <span>Contractor</span>
            <strong>{`${contract.contractor_first_name || ''} ${contract.contractor_last_name || ''}`.trim() || 'Not assigned'}</strong>
            {contract.contractor_company_name ? <small>{contract.contractor_company_name}</small> : null}
          </div>
        </div>

        <div className="homeowner-contract-card__detail">
          <DollarSign aria-hidden="true" />
          <div>
            <span>Contract amount</span>
            <strong>{formatCurrency(contract.project_total_cost || 0)}</strong>
          </div>
        </div>

        <div className="homeowner-contract-card__detail">
          <Calendar aria-hidden="true" />
          <div>
            <span>Timeline</span>
            <strong>{formatDate(contract.contract_start_date)} – {formatDate(contract.contract_completion_date)}</strong>
          </div>
        </div>
      </div>

      <footer className="homeowner-contract-card__actions">
        <Button
          variant="outline"
          className="homeowner-contract-card__button homeowner-contract-card__button--secondary"
          disabled={!contract.contract_url}
          onClick={() => window.open(contract.contract_url, '_blank', 'noopener,noreferrer')}
        >
          <FileText aria-hidden="true" />
          View contract PDF
        </Button>

        <Button
          variant="ghost"
          className="homeowner-contract-card__button homeowner-contract-card__button--secondary"
          onClick={() => navigate(`/create-contract?tab=Project-Details&id=${contract.contract_project_id}`)}
        >
          More details
          <ChevronRight aria-hidden="true" />
        </Button>

        {isReadyToSign ? (
          <Button className="homeowner-contract-card__button homeowner-contract-card__button--primary" onClick={onViewDetails}>
            <PenLine aria-hidden="true" />
            Review &amp; sign
          </Button>
        ) : null}
      </footer>
    </article>
  );
}
